import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { resolvePageGuide, type PageGuide } from './pageGuides.js';

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
}

/**
 * Scrollable user-guide popover shown when hovering (or keyboard-focusing) a page's
 * description line. Content comes from the per-route catalog in pageGuides.ts.
 */
function GuidePopover({ guide }: { guide: PageGuide }): JSX.Element {
  return (
    <div className="absolute left-0 top-full z-50 hidden pt-2 group-hover:block group-focus-within:block">
      <div className="max-h-96 w-[32rem] max-w-[85vw] overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 text-left shadow-xl">
        <h2 className="text-sm font-semibold text-slate-900">{guide.title}</h2>
        {guide.sections.map((section) => (
          <div key={section.heading} className="mt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {section.heading}
            </h3>
            {section.paragraphs?.map((p) => (
              <p key={p} className="mt-1.5 text-sm leading-relaxed text-slate-700">
                {p}
              </p>
            ))}
            {section.bullets ? (
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-700">
                {section.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function PageHeader({ title, description, actions }: PageHeaderProps): JSX.Element {
  const location = useLocation();
  const guide = description ? resolvePageGuide(location.pathname) : null;
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
        {description ? (
          guide ? (
            <div className="group relative inline-block">
              <p
                tabIndex={0}
                className="mt-1 cursor-help text-sm text-slate-500 underline decoration-slate-300 decoration-dotted underline-offset-4 outline-none"
                aria-label={`${description} — hover for the page guide`}
              >
                {description}
              </p>
              <GuidePopover guide={guide} />
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          )
        ) : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}
